import * as React from 'react';
import { useMemo, useState } from 'react';
import * as R from 'ramda';
import { useHistory } from 'react-router-dom';
import { Str } from '../../localization/translationKeys';
import { useMsgText } from '../../localization/hooks';
import ModalHeader from '../../layout/modal/ModalHeader';
import Modal from '../../layout/modal/Modal';
import LocalSelect from '../../localization/LocalSelect';
import { SELECT } from '../../forms/styles/select';
import LocalDropdownOption from '../../localization/LocalDropdownOption';
import { useAppState } from '../../core/redux/hooks';
import LocalDiv from '../../localization/LocalDiv';
import Msg from '../../localization/Msg';
import { css } from '@emotion/core';
import { DevResource } from './models';
import Spacer from '../../layout/Spacer';
import LocalHTMLContainer from '../../localization/LocalHTMLContainer';
import { COLORS } from '../../globalStyle/colors';
import { isNilOrEmpty } from '../../language/module';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import { FLEX_ROW, FLEX_COLUMN } from '../../layout/flexBox';
import { btnStyle } from '../../forms/styles/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';


const styles = {
  root:        css`
    max-width: 530px;
  `,
  content:     [
    FLEX_COLUMN,
    css`
      margin: 0 24px 24px;
    `,
  ],
  select:      css`
    background-color: ${COLORS.WHITE}; 
  `,
  selectRow:   [
    FLEX_ROW,
    css`
      justify-content: flex-start;
    `,
  ],
  button:      [
    btnStyle.primary(COLORS.DARK_GREY),
    css`
      font-size: 24px;
      height: 35px;
      width: 35px;
    `,
  ],
  whiteBottom: css`
    border-bottom 1px solid ${COLORS.WHITE};
    width: 100%;
  `,
  labels:      {
    title: css`
      font-weight: bold;
    `,
    value: css`
      margin-bottom: 20px;
      display: block;
    `,
  },
};


type Props = {
  onDismiss?: () => void,
  className?: string;
};


/**
 * DevResourceModal: a component that renders a project's development resources
 *
 * PureComponent: true
 * Redux Connected: true
 */
const DevResourceModal: React.FC<Props> = props => {
  const history = useHistory();

  const { isMobile } = useWindowSize();

  const header = useMsgText(Str.DevResources);

  const compCats                = useAppState(state => state.devResources || []);
  const [resource, setResource] = useState<DevResource | undefined>(compCats?.[0] ?? undefined);

  const dismiss = props.onDismiss ?? (() => history.goBack());

  const options = useMemo(
    () => R.map(
      compCat => compCat.visibilityOn && (
        <LocalDropdownOption
          key={compCat.competencyId}
          value={compCat.competencyId}
          labelKey={''}
          labelText={compCat.letterCode + '.' + compCat.name}
        />
      ),
      compCats,
    ),
    [compCats],
  );

  const disablePreviousBtn = !compCats.length || resource?.competencyId === compCats[0].competencyId;
  const disableNextBtn     = !compCats.length || resource?.competencyId === compCats[compCats.length - 1].competencyId;

  const selectedIndex = () => R.findIndex(R.propEq('competencyId', resource?.competencyId))(compCats);

  const setPrevious = (selectedIndex: number) => selectedIndex > 0 && setResource(compCats[selectedIndex - 1]);

  const setNext = (
    selectedIndex: number,
  ) => selectedIndex > -1 && selectedIndex < compCats.length && setResource(compCats[selectedIndex + 1]);

  const renderInfo = useMemo(
    () => (
      <LocalDiv>
        {!isNilOrEmpty(resource?.description) && (
          <>
            <Msg str={Str.Description} css={styles.labels.title} />
            <LocalHTMLContainer
              innerHtml={resource?.description || ''}
              css={styles.labels.value}
            />
          </>
        )}
        {!isNilOrEmpty(resource?.scoreHighMessage) && (
          <>
            <Msg str={Str.ScoredHigh} css={styles.labels.title} />
            <LocalHTMLContainer
              innerHtml={resource?.scoreHighMessage || ''}
              css={styles.labels.value}
            />
          </>
        )}
        {!isNilOrEmpty(resource?.scoreHighMessage) && (
          <>
            <Msg str={Str.ScoredLow} css={styles.labels.title} />
            <LocalHTMLContainer
              innerHtml={resource?.scoreLowMessage || ''}
              css={styles.labels.value}
            />
          </>
        )}
        {!isNilOrEmpty(resource?.scoreHighMessage) && (
          <>
            <Msg str={Str.Tips} css={styles.labels.title} />
            <LocalHTMLContainer
              innerHtml={resource?.developmentTips || ''}
              css={styles.labels.value}
            />
          </>
        )}
      </LocalDiv>),
    [resource],
  );

  return (
    <Modal onDismiss={dismiss} dismissable={true} css={styles.root}>
      <ModalHeader title={header} onDismiss={dismiss} />
      <LocalDiv css={styles.content}>
        <LocalDiv>
          <Msg str={Str.Competencies} />
        </LocalDiv>
        <LocalDiv css={styles.selectRow}>
          <LocalSelect
            css={[SELECT, styles.select]}
            name='devResources'
            value={resource?.competencyId || -1}
            onChange={(event) => setResource(compCats.find(c => c.competencyId === +event.target.value))}
            dir='auto'
          >
            {options}
          </LocalSelect>
          {
            !isMobile && (
              <>
                <Spacer sizePx={10} />
                <button
                  css={styles.button}
                  onClick={() => setPrevious(selectedIndex())}
                  disabled={disablePreviousBtn}
                >
                  <FontAwesomeIcon icon={faCaretLeft} />
                </button>
                <Spacer sizePx={10} />
                <button
                  css={styles.button}
                  onClick={() => setNext(selectedIndex())}
                  disabled={disableNextBtn}
                >
                  <FontAwesomeIcon icon={faCaretRight} />
                </button>
              </>
            )
          }
        </LocalDiv>
        <Spacer sizePx={10} css={styles.whiteBottom} />
        <Spacer sizePx={15} />
        {renderInfo}
      </LocalDiv>
    </Modal>
  );
};

export default DevResourceModal;
